import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import { guideSignals } from "@/lib/guideSignals";
import {
  useRescoreMutation,
  useRescoreProgress,
} from "@/hooks/useConceptRefine";

export function useRescoreToast(conceptId: string) {
  const queryClient = useQueryClient();
  const rescoreMutation = useRescoreMutation(conceptId);
  const { progress, subscribe, unsubscribe } = useRescoreProgress(conceptId);
  const loadingToastId = useRef<string | number | undefined>(undefined);

  // Track SSE progress and update the loading toast / show completion
  useEffect(() => {
    if (progress.status === "progress" && loadingToastId.current != null) {
      toast.loading(`Rescoring ${progress.rescored} of ${progress.total} papers...`, {
        id: loadingToastId.current,
      });
    } else if (progress.status === "complete") {
      if (loadingToastId.current != null) {
        toast.dismiss(loadingToastId.current);
        loadingToastId.current = undefined;
      }
      if (progress.outcome === "batched") {
        toast.info("Rescoring in the background", {
          description: "Large rescores run as a background batch — scores update when it finishes, usually within minutes.",
          duration: 8000,
        });
      } else if (progress.outcome === "deferred") {
        toast.info("Rescore not started", {
          description: "Background scoring is already running for this concept. Rescore again once it finishes.",
          duration: 10000,
        });
      } else {
        toast.success(
          `Rescore complete — ${progress.changed} paper${progress.changed === 1 ? "" : "s"} changed score`,
          { duration: 5000 },
        );
      }
      if (progress.outcome !== "deferred") guideSignals.mark(conceptId, "hasRescoredAfterRefine");
      queryClient.invalidateQueries({ queryKey: ["papers", conceptId] });
      // Surfaces the background-batch pill (and its completion toast) now.
      if (progress.outcome === "batched") queryClient.invalidateQueries({ queryKey: ["scoring-batch", conceptId] });
      unsubscribe();
    } else if (progress.status === "error") {
      if (loadingToastId.current != null) {
        toast.dismiss(loadingToastId.current);
        loadingToastId.current = undefined;
      }
      toast.error("Rescore failed", {
        description: progress.errorMessage || "An error occurred",
      });
      unsubscribe();
    }
  }, [progress, conceptId, queryClient, unsubscribe]);

  // Clean up SSE on unmount
  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  const promptRescore = useCallback(() => {
    toast("Concept refined — rescore corpus?", {
      duration: 15000,
      action: {
        label: "Rescore",
        onClick: () => {
          const id = toast.loading("Starting rescore...");
          loadingToastId.current = id;

          rescoreMutation.mutate(undefined, {
            onSuccess: (data) => {
              toast.loading("Rescoring papers...", { id });
              subscribe(data.job_id);
            },
            onError: (err) => {
              toast.dismiss(id);
              loadingToastId.current = undefined;
              toast.error("Failed to start rescore", {
                description:
                  err instanceof Error ? err.message : "Unknown error",
              });
            },
          });
        },
      },
    });
  }, [rescoreMutation, subscribe]);

  return { promptRescore };
}
